import React, { useEffect, useState, useCallback } from "react";
import { api, errMsg, fmtNum, curSym, fmtDate } from "@finance/lib/api";
import { toast } from "sonner";
import Layout, { PageHeader } from "@finance/components/Layout";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { Label } from "@finance/components/ui/label";
import { useAuth } from "@finance/context/AuthContext";
import { ArrowsClockwise, ClockCounterClockwise, Scales, CheckCircle } from "@phosphor-icons/react";

const TABS = [
  { key: "realized", label: "Realized FX", icon: CheckCircle },
  { key: "revaluation", label: "Period-End Revaluation", icon: Scales },
  { key: "catchup", label: "Historical Catch-Up", icon: ClockCounterClockwise },
];

const GL = (n) => (n > 0 ? "text-emerald-600" : n < 0 ? "text-red-600" : "text-gray-500");

export default function Forex() {
  const { user } = useAuth();
  const isExec = !!user?.is_executive;
  const [tab, setTab] = useState("realized");
  return (
    <Layout>
      <PageHeader title="Forex" subtitle="Realized settlement gains/losses, period-end revaluation, and historical catch-up — all TZS-based" />
      <div className="p-8">
        <div className="mb-6 flex flex-wrap gap-2" data-testid="fx-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`fx-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                tab === t.key ? "bg-[#1B4383] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              <t.icon size={16} weight="bold" /> {t.label}
            </button>
          ))}
        </div>
        {tab === "realized" && <RealizedTab />}
        {tab === "revaluation" && <RevaluationTab />}
        {tab === "catchup" && <CatchupTab isExec={isExec} />}
      </div>
    </Layout>
  );
}

function EmptyState({ text }) {
  return <div className="py-12 text-center text-sm text-gray-400" data-testid="fx-empty">{text}</div>;
}

function RealizedTab() {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/fx/realized").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <EmptyState text="Loading…" />;
  return (
    <div className="rounded-xl bg-white p-6 shadow-sm" data-testid="fx-realized-card">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <div className="lx-label text-gray-500">Net realized FX (gain +/loss −)</div>
          <div className={`font-display text-2xl font-bold ${GL(data.net_gain_loss)}`} data-testid="fx-realized-net">
            {fmtNum(data.net_gain_loss, "TZS")}
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load}><ArrowsClockwise size={14} className="mr-1" />Refresh</Button>
      </div>
      {data.rows.length === 0 ? (
        <EmptyState text="No foreign-currency settlements yet. Realized gains/losses appear here the moment a foreign invoice is paid or received at a rate different from its booking rate." />
      ) : (
        <table className="w-full text-sm">
          <thead className="lx-label border-b border-gray-100 text-gray-500">
            <tr><th className="py-2 text-left">Date</th><th className="text-left">Direction</th><th className="text-left">Party</th>
              <th className="text-right">Amount</th><th className="text-right">Booking</th><th className="text-right">Settle</th><th className="text-right">Gain/Loss (TZS)</th></tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.payment_id} className="border-b border-gray-50" data-testid={`fx-realized-row-${r.payment_id}`}>
                <td className="py-2">{fmtDate(r.date)}</td>
                <td className="capitalize">{r.direction}</td>
                <td>{r.payee || "—"}</td>
                <td className="text-right">{curSym(r.currency)} {fmtNum(r.amount)}</td>
                <td className="text-right">{fmtNum(r.booking_rate)}</td>
                <td className="text-right">{fmtNum(r.settlement_rate)}</td>
                <td className={`text-right font-medium ${GL(r.realized_gain_loss)}`}>{fmtNum(r.realized_gain_loss)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function RevaluationTab() {
  const now = new Date();
  const [period, setPeriod] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [override, setOverride] = useState("");
  const [prev, setPrev] = useState(null);
  const [busy, setBusy] = useState(false);

  const buildOverrides = () => {
    const o = {};
    override.split(",").map((s) => s.trim()).filter(Boolean).forEach((pair) => {
      const [cur, rate] = pair.split(":").map((x) => x.trim());
      if (cur && rate && !Number.isNaN(Number(rate))) o[cur.toUpperCase()] = Number(rate);
    });
    return o;
  };

  const preview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/fx/revaluation/preview", { period, rate_overrides: buildOverrides() });
      setPrev(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl bg-white p-6 shadow-sm" data-testid="fx-reval-controls">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <Label className="lx-label">Period (YYYY-MM)</Label>
            <Input className="mt-1 w-40" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="fx-reval-period" placeholder="2026-08" />
          </div>
          <div className="flex-1 min-w-[220px]">
            <Label className="lx-label">Manual closing rate override (optional)</Label>
            <Input className="mt-1" value={override} onChange={(e) => setOverride(e.target.value)} data-testid="fx-reval-override" placeholder="USD:2650, EUR:2900" />
          </div>
          <Button onClick={preview} disabled={busy} data-testid="fx-reval-preview" className="bg-gray-900 text-white hover:bg-gray-800">
            <Scales size={15} className="mr-1" />{busy ? "Working…" : "Preview"}
          </Button>
        </div>
        <p className="mt-3 text-xs text-gray-400">Rates default to the FX feed on-or-before period-end. Leave override blank to use the feed. Nothing posts until you approve.</p>
      </div>

      {prev && (
        <div className="rounded-xl bg-white p-6 shadow-sm" data-testid="fx-reval-preview-card">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <div className="lx-label text-gray-500">Unrealized FX for {prev.period} (period-end {fmtDate(prev.period_end)})</div>
              <div className={`font-display text-2xl font-bold ${GL(prev.net_gain_loss)}`} data-testid="fx-reval-net">{fmtNum(prev.net_gain_loss, "TZS")}</div>
              <div className="mt-1 text-xs text-gray-400">Reverses automatically on {fmtDate(prev.reversal_date)} · period is {prev.period_status}</div>
            </div>
            {prev.rows.length > 0 && (
              <div className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700" data-testid="fx-reval-note">
                Planning preview only. This posts when you <b>Close</b> {prev.period} permanently in Period Lock &amp; Close (with your approval there).
              </div>
            )}
          </div>
          {prev.rows.length === 0 ? (
            <EmptyState text="No open foreign-currency balances to revalue for this period." />
          ) : (
            <table className="w-full text-sm">
              <thead className="lx-label border-b border-gray-100 text-gray-500">
                <tr><th className="py-2 text-left">Account</th><th className="text-left">Class</th><th className="text-left">Cur</th>
                  <th className="text-right">FC balance</th><th className="text-right">Rate</th><th className="text-right">Booked TZS</th><th className="text-right">Revalued TZS</th><th className="text-right">Unrealized</th></tr>
              </thead>
              <tbody>
                {prev.rows.map((r, i) => (
                  <tr key={i} className="border-b border-gray-50" data-testid={`fx-reval-row-${i}`}>
                    <td className="py-2">{r.account_name} <span className="text-gray-400">{r.account_code}</span></td>
                    <td className="uppercase text-gray-500">{r.class}</td>
                    <td>{r.currency}</td>
                    <td className="text-right">{fmtNum(r.foreign_balance)}</td>
                    <td className="text-right">{fmtNum(r.rate)}<span className="ml-1 text-[10px] text-gray-400">{r.rate_source}</span></td>
                    <td className="text-right">{fmtNum(r.booked_tzs)}</td>
                    <td className="text-right">{fmtNum(r.revalued_tzs)}</td>
                    <td className={`text-right font-medium ${GL(r.unrealized_gain_loss)}`}>{fmtNum(r.unrealized_gain_loss)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function CatchupTab({ isExec }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/fx/catchup/preview").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);

  const post = async () => {
    if (!window.confirm("Post catch-up FX entries for all listed items? This clears the residual in Payables/Receivables into the Forex accounts.")) return;
    setBusy(true);
    try {
      const { data: res } = await api.post("/fx/catchup/post", {});
      toast.success(`Posted ${res.posted} catch-up entries. Net ${fmtNum(res.net_gain_loss)} TZS.`);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (!data) return <EmptyState text="Loading…" />;
  return (
    <div className="rounded-xl bg-white p-6 shadow-sm" data-testid="fx-catchup-card">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <div className="lx-label text-gray-500">Legacy settlements missing FX treatment</div>
          <div className={`font-display text-2xl font-bold ${GL(data.net_gain_loss)}`} data-testid="fx-catchup-net">{fmtNum(data.net_gain_loss, "TZS")}</div>
          {data.locked_periods?.length > 0 && (
            <div className="mt-1 text-xs text-red-500">Skipped (locked periods): {data.locked_periods.join(", ")} — unlock to include them.</div>
          )}
        </div>
        {isExec && data.count > 0 && (
          <Button onClick={post} disabled={busy} data-testid="fx-catchup-post" className="bg-[#1B4383] text-white hover:bg-[#122E5C]">
            <CheckCircle size={15} className="mr-1" />Post {data.count} catch-up entries
          </Button>
        )}
      </div>
      {data.count === 0 ? (
        <EmptyState text="Nothing to reconcile — every settled foreign payment already carries correct FX treatment." />
      ) : (
        <table className="w-full text-sm">
          <thead className="lx-label border-b border-gray-100 text-gray-500">
            <tr><th className="py-2 text-left">Date</th><th className="text-left">Direction</th><th className="text-left">Party</th><th className="text-left">Period</th>
              <th className="text-right">Amount</th><th className="text-right">Booking</th><th className="text-right">Settle</th><th className="text-right">Gain/Loss (TZS)</th></tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.payment_id} className="border-b border-gray-50" data-testid={`fx-catchup-row-${r.payment_id}`}>
                <td className="py-2">{fmtDate(r.date)}</td>
                <td className="capitalize">{r.direction}</td>
                <td>{r.payee || "—"}</td>
                <td>{r.period} <span className={r.period_status !== "open" ? "text-red-500" : "text-gray-400"}>({r.period_status})</span></td>
                <td className="text-right">{curSym(r.currency)} {fmtNum(r.amount_fc)}</td>
                <td className="text-right">{fmtNum(r.booking_rate)}</td>
                <td className="text-right">{fmtNum(r.settlement_rate)}</td>
                <td className={`text-right font-medium ${GL(r.realized_gain_loss)}`}>{fmtNum(r.realized_gain_loss)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
